import { useState, type FormEvent } from 'react';
import { Link, useNavigate } from 'react-router-dom';

import { ApiError } from '../../lib/api-client';
import { PatientUhidAutocomplete } from '../../components/ui/PatientUhidAutocomplete';
import { FieldError } from '../../components/ui/FieldError';
import { usePatientLookupSearch, usePatientUhidSearch } from '../../lib/patient-uhid-search';
import { useToast } from '../../lib/toast';
import { useCreateIpdDaycareAdmission, useIpdDaycareLookup } from './ipd-daycare.queries';
import {
  AdmissionInfoFields,
  FieldLabel,
  PatientDemographicFields,
  PatientIdentityFields,
  ResponsiblePersonFields,
} from './ipd-daycare.shared';
import { blankPatientFields, blankResponsiblePerson, dateTimeInput, patientFieldsFromOption } from './ipd-daycare.utils';
import type { CreateIpdDaycareAdmissionPayload, PatientFormFields } from './types';

interface AdmissionInfoState {
  symptoms: string | null;
  diagnosisAtAdmission: string | null;
  underDoctor: string | null;
  otherUnderDoctor: string | null;
  bedCategory: string | null;
  bed: string | null;
  alcoholTobaccoHistory: string | null;
  pastMedicalSurgicalHistory: string | null;
  familyHistory: string | null;
  referredBy: string | null;
  provider: string | null;
  marketBy: string | null;
}

const blankAdmissionInfo = (): AdmissionInfoState => ({
  symptoms: null, diagnosisAtAdmission: null, underDoctor: null, otherUnderDoctor: null, bedCategory: null, bed: null,
  alcoholTobaccoHistory: null, pastMedicalSurgicalHistory: null, familyHistory: null, referredBy: null, provider: null, marketBy: null,
});

const validateAdmissionFields = (
  values: { admissionDate: string; packageType: string; insuranceType: string },
  patient: PatientFormFields,
  admissionInfo: AdmissionInfoState,
  responsiblePerson: ReturnType<typeof blankResponsiblePerson>,
) => {
  const errors: Record<string, string> = {};
  if (!values.admissionDate) errors.admissionDate = 'Admission Date & time is required.';
  if (!values.packageType) errors.packageType = 'Package Type is required.';
  if (!patient.name.trim()) errors.name = "Patient's name is required.";
  if ((patient.phone ?? '').trim() && !/^\d{10}$/.test((patient.phone ?? '').trim())) errors.phone = 'Enter a 10 digit phone number.';
  if ((patient.alternativeNumber ?? '').trim() && !/^\d{10}$/.test((patient.alternativeNumber ?? '').trim())) errors.alternativeNumber = 'Enter a 10 digit phone number.';
  if (!(patient.guardianName ?? '').trim()) errors.guardianName = 'Guardian Name is required.';
  if (!patient.relation) errors.relation = 'Relation is required.';
  if (!(patient.address ?? '').trim()) errors.address = 'Address is required.';
  if (!(patient.state ?? '').trim()) errors.state = 'State is required.';
  if (!admissionInfo.underDoctor) errors.underDoctor = 'Under Doctor is required.';
  if (!admissionInfo.bedCategory) errors.bedCategory = 'Bed Category is required.';
  if (!admissionInfo.bed) errors.bed = 'Bed is required.';
  if (!admissionInfo.referredBy) errors.referredBy = 'Referred By is required.';
  if (responsiblePerson.phone.trim() && !/^\d{10}$/.test(responsiblePerson.phone.trim())) errors.responsiblePersonPhone = 'Enter a 10 digit phone number.';
  return errors;
};

export const IpdDaycareAdmissionPage = () => {
  const navigate = useNavigate();
  const mutation = useCreateIpdDaycareAdmission();
  const { showToast } = useToast();

  const [admissionType, setAdmissionType] = useState<'IPD' | 'Daycare'>('IPD');
  const [admissionDate, setAdmissionDate] = useState(dateTimeInput);
  const [packageType, setPackageType] = useState('');
  const [insuranceType, setInsuranceType] = useState('');
  const [insuranceNumber, setInsuranceNumber] = useState('');

  const [patientSearch, setPatientSearch] = useState('');
  const debouncedPatientSearch = usePatientUhidSearch(patientSearch);
  const patients = useIpdDaycareLookup('patients', debouncedPatientSearch);
  const [patient, setPatient] = useState<PatientFormFields>(blankPatientFields);
  const debouncedPhoneSearch = usePatientLookupSearch(patient.phone ?? '');
  const debouncedNameSearch = usePatientLookupSearch(patient.name);
  const phonePatients = useIpdDaycareLookup('patients-phone', debouncedPhoneSearch);
  const namePatients = useIpdDaycareLookup('patients-name', debouncedNameSearch);
  const states = useIpdDaycareLookup('states');
  const districts = useIpdDaycareLookup('districts', '', { state: patient.state ?? '' });

  const [admissionInfo, setAdmissionInfo] = useState<AdmissionInfoState>(blankAdmissionInfo);
  const doctors = useIpdDaycareLookup('doctors');
  const wards = useIpdDaycareLookup('wards');
  const beds = useIpdDaycareLookup('beds', '', { ward: admissionInfo.bedCategory ?? '' });
  const tpas = useIpdDaycareLookup('tpas');
  const referrals = useIpdDaycareLookup('referrals');
  const providers = useIpdDaycareLookup('providers');
  const marketByOptions = useIpdDaycareLookup('market-by');
  const insuranceTypeOptions = tpas.data ?? [];

  const [responsiblePerson, setResponsiblePerson] = useState(blankResponsiblePerson);
  const [showErrors, setShowErrors] = useState(false);
  const errors = showErrors ? validateAdmissionFields({ admissionDate, packageType, insuranceType }, patient, admissionInfo, responsiblePerson) : {};

  const choosePatient = (id: string) => {
    const option = patients.data?.find((item) => item.id === id);
    if (!option) { setPatient(blankPatientFields()); return; }
    setPatient(patientFieldsFromOption(option));
  };
  const changePatientSearch = (value: string) => { setPatientSearch(value); if (patient.patientId) setPatient(blankPatientFields()); };
  const choosePatientFrom = (options: typeof patients.data, id: string) => {
    const option = options?.find((item) => item.id === id);
    if (!option) {
      setPatient(blankPatientFields());
      setPatientSearch('');
      return;
    }
    setPatient(patientFieldsFromOption(option));
    setPatientSearch(option.code ?? '');
  };
  const changePatientField = (field: 'name' | 'phone', value: string) => {
    if (patient.patientId) setPatientSearch('');
    setPatient((current) => {
      const next = current.patientId ? blankPatientFields() : current;
      return { ...next, [field]: field === 'phone' ? value || null : value };
    });
  };

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    setShowErrors(true);
    if (Object.keys(validateAdmissionFields({ admissionDate, packageType, insuranceType }, patient, admissionInfo, responsiblePerson)).length > 0) return;
    const payload: CreateIpdDaycareAdmissionPayload = {
      ...patient,
      admissionType,
      admissionDate,
      packageType,
      insuranceType,
      insuranceNumber: insuranceNumber || null,
      symptoms: admissionInfo.symptoms,
      diagnosisAtAdmission: admissionInfo.diagnosisAtAdmission,
      underDoctor: admissionInfo.underDoctor,
      otherUnderDoctor: admissionInfo.otherUnderDoctor,
      bedCategory: admissionInfo.bedCategory,
      bed: admissionInfo.bed,
      alcoholTobaccoHistory: admissionInfo.alcoholTobaccoHistory,
      pastMedicalSurgicalHistory: admissionInfo.pastMedicalSurgicalHistory,
      familyHistory: admissionInfo.familyHistory,
      referredBy: admissionInfo.referredBy,
      provider: admissionInfo.provider,
      marketBy: admissionInfo.marketBy,
      responsiblePerson,
    };
    try {
      const created = await mutation.mutateAsync(payload);
      showToast('IPD/Daycare admission saved successfully.', 'success');
      void navigate(`/ipd-daycare/${created.id}`);
    } catch (error) {
      showToast(error instanceof ApiError ? error.message : 'IPD/Daycare admission could not be saved.', 'error');
    }
  };

  const busy = mutation.isPending;
  return (
    <div className="page-stack ipd-daycare-page">
      <div className="page-heading"><div><p className="eyebrow">IPD / Daycare</p><h1>IPD / Daycare Admission</h1><p>Patient details, admission information, and responsible person.</p></div><Link className="button button-secondary" to="/ipd-daycare">Back to IPD / Daycare List</Link></div>
      <form className="content-card ipd-daycare-registration-card" noValidate onSubmit={(event) => void submit(event)}>
        <section className="ipd-daycare-section">
          <div className="ipd-daycare-top-row">
            <label><FieldLabel required>Admission Type</FieldLabel><select value={admissionType} onChange={(event) => setAdmissionType(event.target.value as 'IPD' | 'Daycare')} required><option>IPD</option><option>Daycare</option></select></label>
            <label><FieldLabel required>Admission Date &amp; time</FieldLabel><input type="datetime-local" value={admissionDate} onChange={(event) => setAdmissionDate(event.target.value)} required aria-invalid={Boolean(errors.admissionDate)} /><FieldError message={errors.admissionDate} /></label>
            <label><FieldLabel required>Package Type</FieldLabel><select value={packageType} onChange={(event) => setPackageType(event.target.value)} required aria-invalid={Boolean(errors.packageType)}><option value="">Select</option><option>General Package</option><option>Package with Medicine</option><option>Conservative Medicine</option><option>Surgical Package</option></select><FieldError message={errors.packageType} /></label>
            <label><FieldLabel>Insurance Type</FieldLabel><select aria-label="Insurance Type" value={insuranceType} onChange={(event) => setInsuranceType(event.target.value)} aria-invalid={Boolean(errors.insuranceType)}><option value="">Select</option>{insuranceTypeOptions.map((option) => <option key={option.id} value={option.label}>{option.label}</option>)}</select><FieldError message={errors.insuranceType} /></label>
            <label><FieldLabel>Insurance No.</FieldLabel><input value={insuranceNumber} onChange={(event) => setInsuranceNumber(event.target.value)} maxLength={100} /></label>
          </div>
        </section>
        <section className="ipd-daycare-section"><h2>Patient Details</h2>
          <div className="ipd-daycare-patient-row1">
            <label><FieldLabel>UHID</FieldLabel><PatientUhidAutocomplete id="ipd-daycare-patient" value={patientSearch} selectedId={patient.patientId} options={patients.data} isFetching={patients.isFetching} isError={patients.isError} placeholder="Search by UHID" onInputChange={changePatientSearch} onChange={choosePatient} /></label>
            <PatientIdentityFields
              value={patient}
              onChange={setPatient}
              errors={errors}
              mobileField={<PatientUhidAutocomplete id="ipd-daycare-patient-phone" mode="phone" value={patient.phone ?? ''} selectedId={patient.patientId} options={phonePatients.data} isFetching={phonePatients.isFetching} isError={phonePatients.isError} onInputChange={(value) => changePatientField('phone', value)} onChange={(id) => choosePatientFrom(phonePatients.data, id)} />}
              nameField={<PatientUhidAutocomplete id="ipd-daycare-patient-name" mode="name" value={patient.name} selectedId={patient.patientId} options={namePatients.data} isFetching={namePatients.isFetching} isError={namePatients.isError} placeholder="Type patient name" ariaInvalid={Boolean(errors.name)} onInputChange={(value) => changePatientField('name', value)} onChange={(id) => choosePatientFrom(namePatients.data, id)} />}
            />
          </div>
          <div className="ipd-daycare-patient-row2">
            <PatientDemographicFields value={patient} onChange={setPatient} states={states.data} districts={districts.data} errors={errors} />
          </div>
        </section>
        <section className="ipd-daycare-section"><h2>Patient Admission Information</h2>
          <div className="ipd-daycare-admission-row">
            <AdmissionInfoFields
              value={admissionInfo}
              onChange={setAdmissionInfo}
              doctors={doctors.data}
              wards={wards.data}
              beds={beds.data}
              referrals={referrals.data}
              providers={providers.data}
              marketByOptions={marketByOptions.data}
              errors={errors}
            />
          </div>
        </section>
        <section className="ipd-daycare-section"><h2>Responsible Person Details</h2>
          <div className="ipd-daycare-responsible-row">
            <ResponsiblePersonFields value={responsiblePerson} onChange={setResponsiblePerson} errors={errors} />
          </div>
        </section>
        <div className="ipd-daycare-form-actions"><button className="button button-primary" type="submit" disabled={busy}>{busy ? 'Saving...' : 'Save'}</button></div>
      </form>
    </div>
  );
};
